import { type CHCameraId, type SiteId } from '@solinkcorp/api-types';
import { validateDateInFuture } from '@solinkcorp/utils';
import { type HistogramRange } from '@solinkcorp/vd-common';
import AnalyticsLogger from 'models/AnalyticsLogger';
import moment from 'moment';
import { histogramRangeToMomentDuration } from 'utils/EventsFetchUtil';
import { type ThunkAction } from './index';
export interface EventFilter {
  date?: number;
  range?: HistogramRange;
  locationIds?: readonly SiteId[];
  locationTags?: readonly string[];
  timezoneId?: string;
  cameraGroupId?: string | null;
  selectedCameraIds?: readonly CHCameraId[];
  selectedLocationId?: string | null;
  cameraSelectMode?: CameraSelectMode;
  spotCheckMode?: SpotCheckMode;
  spotCheckTitle?: string;
}

interface SetFilterDateAction {
  type: 'FILTER_SET_DATE';
  date: number;
}

interface SetRangeAction {
  type: 'FILTER_SET_RANGE';
  range: HistogramRange;
}

interface SetLocationsAction {
  type: 'FILTER_SET_LOCATIONS';
  locationIds: SiteId[];
}

interface SetLocationTagsAction {
  type: 'FILTER_SET_LOCATION_TAGS';
  locationTags: string[];
}
interface SetCameraGroupAction {
  type: 'FILTER_SET_CAMERA_GROUP';
  cameraGroupId: string;
}

interface ResetCameraGroupAction {
  type: 'RESET_CAMERA_GROUP';
}

interface SetFilterAction extends EventFilter {
  type: 'FILTER_SET';
}

interface SetTimezoneIdAction {
  type: 'SET_TIMEZONE_ID';
  timezoneId: string;
}

export type CameraSelectMode = 'video' | 'spot-check' | null;

export type SpotCheckMode = 'scheduled' | 'live-preview' | 'event' | null;

/**
 * Set the filter date shown in the upper left hand corner of the Events, Clips,
 * Video, and Discover pages.
 *
 * Note that this should (ideally) only be called in response to a user action.
 * If we load new events, and cause the event grid to change, we shouldn't update
 * the filter date - instead we should be using the filter date to drive what
 * events are loaded and what event we are currently displaying.
 */
export function setFilterDate(date: Date | number): SetFilterDateAction {
  const validatedDate = validateDateInFuture(date);
  return {
    type: 'FILTER_SET_DATE',
    date: validatedDate,
  };
}

export function setRange(range: HistogramRange): SetRangeAction {
  return {
    type: 'FILTER_SET_RANGE',
    range,
  };
}

export function setLocations(locationIds: SiteId[]): SetLocationsAction {
  return {
    type: 'FILTER_SET_LOCATIONS',
    locationIds,
  };
}

export function setLocationTags(locationTags: string[]): SetLocationTagsAction {
  return {
    type: 'FILTER_SET_LOCATION_TAGS',
    locationTags,
  };
}

export function setCameraGroup(cameraGroupId: string): SetCameraGroupAction {
  return {
    type: 'FILTER_SET_CAMERA_GROUP',
    cameraGroupId,
  };
}

export function resetCameraGroup(): ResetCameraGroupAction {
  return { type: 'RESET_CAMERA_GROUP' };
}

export function setFilter(filter: EventFilter): SetFilterAction {
  return {
    type: 'FILTER_SET',
    ...filter,
  };
}

export function setTimezoneId(timezoneId: string): SetTimezoneIdAction {
  return {
    type: 'SET_TIMEZONE_ID',
    timezoneId,
  };
}

/**
 * Increments the `date` by 1 `unit`. Sets the filter to this modified date.
 * @param date - Initial date
 * @param unit - Unit in which to increase the date
 */
export function incrementDate(params: { date?: Date | number; unit: HistogramRange }): ThunkAction {
  return (dispatch, getState) => {
    const { unit } = params;
    const date = params.date || getState().filter.date;
    const newDate = moment(date).add(1, histogramRangeToMomentDuration(unit)).toDate();
    AnalyticsLogger.selectDateViaIncrement({ date: newDate });
    dispatch(setFilterDate(newDate));
  };
}

/**
 * Decrements the `date` by 1 `unit`. Sets the filter to this modified date.
 * @param date - Initial date
 * @param unit - Unit in which to decrease the date
 */
export function decrementDate(params: {
  date?: Date | number;
  unit: HistogramRange;
  minDate?: Date | null | undefined;
}): ThunkAction {
  return (dispatch, getState) => {
    const { unit, minDate } = params;
    const date = params.date || getState().filter.date;
    const targetDate = moment(date).subtract(1, histogramRangeToMomentDuration(unit)).toDate();
    const newDate = minDate && targetDate < minDate ? minDate : targetDate;
    AnalyticsLogger.selectDateViaDecrement({ date: newDate });
    dispatch(setFilterDate(newDate));
  };
}
// Union type of all actions from `generalActions`.
export type FilterActionTypes =
  | SetFilterDateAction
  | SetRangeAction
  | SetLocationsAction
  | SetLocationTagsAction
  | SetCameraGroupAction
  | SetFilterAction
  | SetTimezoneIdAction
  | ResetCameraGroupAction;
